import type { DocumentTemplateArchivedAction } from '@Application/Documents/Templates/Actions/documentTemplateArchived';
import type { DocumentTemplateCreatedAction } from '@Application/Documents/Templates/Actions/documentTemplateCreated';
import type { DocumentTemplateUpdatedAction } from '@Application/Documents/Templates/Actions/documentTemplateUpdated';
import type { DocumentTemplateVersionFetchedAction } from '@Application/Documents/Templates/Actions/documentTemplateVersionFetched';
import type { DocumentTemplateVersionSectionsUpdatedAction } from '@Application/Documents/Templates/Actions/documentTemplateVersionSectionsUpdated';
import type { DocumentTemplateVersionUploadedAction } from '@Application/Documents/Templates/Actions/documentTemplateVersionUploaded';
import type { DocumentTemplateVersionsFetchedAction } from '@Application/Documents/Templates/Actions/documentTemplateVersionsFetched';
import type { DocumentTemplatesFetchedAction } from '@Application/Documents/Templates/Actions/documentTemplatesFetched';
import type { DocumentTypeArchivedAction } from '@Application/Documents/DocumentTypes/Actions/documentTypeArchived';
import type { DocumentTypeCreatedAction } from '@Application/Documents/DocumentTypes/Actions/documentTypeCreated';
import type { DocumentTypeRestoredAction } from '@Application/Documents/DocumentTypes/Actions/documentTypeRestored';
import type { FacilityTemplateOverrideRemovedAction } from '@Application/Documents/TemplateAssignments/Actions/facilityTemplateOverrideRemoved';
import type { FacilityTemplateOverridesFetchedAction } from '@Application/Documents/TemplateAssignments/Actions/facilityTemplateOverridesFetched';
import type { TemplateAssignmentClearedAction } from '@Application/Documents/TemplateAssignments/Actions/templateAssignmentCleared';
import type { TemplateAssignmentSavedAction } from '@Application/Documents/TemplateAssignments/Actions/templateAssignmentSaved';
import type { TemplateAssignmentsFetchedAction } from '@Application/Documents/TemplateAssignments/Actions/templateAssignmentsFetched';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import DocumentTemplateVersionDetailView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateVersionDetailView';
import DocumentTemplateVersionView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateVersionView';
import DocumentTemplateVersionsListView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateVersionsListView';
import DocumentTemplateView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateView';
import DocumentTemplatesListView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplatesListView';
import FacilityTemplateOverrideView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/FacilityTemplateOverrideView';
import FacilityTemplateOverridesListView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/FacilityTemplateOverridesListView';
import TemplateAssignmentView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/TemplateAssignmentView';
import TemplateAssignmentsListView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/TemplateAssignmentsListView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type DocumentTemplatesStore = {
	templates: DocumentTemplatesListView | null;
	templateVersions: DocumentTemplateVersionsListView | null;
	selectedVersion: DocumentTemplateVersionDetailView | null;
	organizationAssignments: TemplateAssignmentsListView | null;
	facilityAssignments: TemplateAssignmentsListView | null;
	facilityOverrides: FacilityTemplateOverridesListView | null;
};

const initialState: DocumentTemplatesStore = {
	templates: null,
	templateVersions: null,
	selectedVersion: null,
	organizationAssignments: null,
	facilityAssignments: null,
	facilityOverrides: null,
};

type Actions =
	| DocumentTemplatesFetchedAction
	| DocumentTemplateVersionsFetchedAction
	| DocumentTemplateVersionFetchedAction
	| DocumentTemplateCreatedAction
	| DocumentTemplateVersionUploadedAction
	| DocumentTemplateVersionSectionsUpdatedAction
	| DocumentTemplateUpdatedAction
	| DocumentTemplateArchivedAction
	| TemplateAssignmentsFetchedAction
	| TemplateAssignmentSavedAction
	| TemplateAssignmentClearedAction
	| FacilityTemplateOverridesFetchedAction
	| FacilityTemplateOverrideRemovedAction
	| DocumentTypeCreatedAction
	| DocumentTypeArchivedAction
	| DocumentTypeRestoredAction
	| UserRevokedAction;

const whenDocumentTemplatesFetched = (
	store: DocumentTemplatesStore,
	action: DocumentTemplatesFetchedAction,
): DocumentTemplatesStore => ({
	...store,
	templates: new DocumentTemplatesListView(
		action.payload.map((entry) => new DocumentTemplateView(entry)),
		action.organizationId,
	),
});

const whenDocumentTemplateVersionsFetched = (
	store: DocumentTemplatesStore,
	action: DocumentTemplateVersionsFetchedAction,
): DocumentTemplatesStore => ({
	...store,
	templateVersions: new DocumentTemplateVersionsListView(
		action.payload.map((entry) => new DocumentTemplateVersionView(entry)),
		action.organizationId,
	),
});

const whenDocumentTemplateVersionFetched = (
	store: DocumentTemplatesStore,
	action: DocumentTemplateVersionFetchedAction,
): DocumentTemplatesStore => ({
	...store,
	selectedVersion: new DocumentTemplateVersionDetailView(action.payload),
});

const whenDocumentTemplateVersionSectionsUpdated = (
	store: DocumentTemplatesStore,
	action: DocumentTemplateVersionSectionsUpdatedAction,
): DocumentTemplatesStore => ({
	...store,
	selectedVersion: new DocumentTemplateVersionDetailView(action.payload),
});

// Neither response carries the full template shape — both return the version, not the template:
// there is nothing to merge into `templates`/`versions` locally, so both are invalidated and the
// next read refetches.
const whenDocumentTemplateCreated = (store: DocumentTemplatesStore): DocumentTemplatesStore => ({
	...store,
	templates: null,
	templateVersions: null,
});

const whenDocumentTemplateVersionUploaded = (store: DocumentTemplatesStore): DocumentTemplatesStore => ({
	...store,
	templates: null,
	templateVersions: null,
});

const whenDocumentTemplateUpdated = (
	store: DocumentTemplatesStore,
	action: DocumentTemplateUpdatedAction,
): DocumentTemplatesStore => {
	if (store.templates === null || store.templates.organizationId !== action.organizationId) {
		return store;
	}

	return { ...store, templates: store.templates.update(new DocumentTemplateView(action.payload)) };
};

// Archiving is refused server-side while the template is assigned, so archiving never changes an
// assignment or an override. Only `templates`/`versions` are invalidated.
const whenDocumentTemplateArchived = (store: DocumentTemplatesStore): DocumentTemplatesStore => ({
	...store,
	templates: null,
	templateVersions: null,
});

const whenTemplateAssignmentsFetched = (
	store: DocumentTemplatesStore,
	action: TemplateAssignmentsFetchedAction,
): DocumentTemplatesStore => {
	const list = new TemplateAssignmentsListView(
		action.payload.map((entry) => new TemplateAssignmentView(entry, action.scope.type)),
		action.scope.id,
		action.scope.type,
	);

	if (action.scope.type === 'organization') {
		return { ...store, organizationAssignments: list };
	}

	return { ...store, facilityAssignments: list };
};

// Neither a save nor a clear is merged locally: a `204` carries no body, and even the `200` of a save
// cannot tell what now applies one level down (an organization-level change reshapes every facility's
// inheritance). Both invalidate instead of computing a replacement (`state-stores.md`, "Étendre un
// store").
const whenTemplateAssignmentInvalidated = (
	store: DocumentTemplatesStore,
	action: TemplateAssignmentSavedAction | TemplateAssignmentClearedAction,
): DocumentTemplatesStore => {
	if (action.scope.type === 'organization') {
		return { ...store, organizationAssignments: null, facilityAssignments: null };
	}

	return { ...store, facilityAssignments: null, facilityOverrides: null };
};

const whenFacilityTemplateOverridesFetched = (
	store: DocumentTemplatesStore,
	action: FacilityTemplateOverridesFetchedAction,
): DocumentTemplatesStore => ({
	...store,
	facilityOverrides: new FacilityTemplateOverridesListView(
		action.payload.map((entry) => new FacilityTemplateOverrideView(entry)),
		action.organizationId,
	),
});

// The removal does not say which inherited template now applies in the affected facility: `state-
// stores.md` prescribes invalidation over a locally-computed replacement.
const whenFacilityTemplateOverrideRemoved = (store: DocumentTemplatesStore): DocumentTemplatesStore => ({
	...store,
	facilityOverrides: null,
	facilityAssignments: null,
});

// The affectation endpoint answers with one entry per ACTIVE document type: creating, archiving or
// restoring a type changes that list, even though the type catalogue itself lives in a separate store
// (`documentTypesStore`). Renaming a type never reaches here — the assignments keep the same
// identifiers, and the label they display is resolved from `documentTypesStore` at render time.
const whenAssignedDocumentTypeCatalogueChanged = (store: DocumentTemplatesStore): DocumentTemplatesStore => ({
	...store,
	organizationAssignments: null,
	facilityAssignments: null,
});

// Every slot here is organization- or facility-scoped administration data: none of it should survive
// a logout and be served to the next person on the same browser.
const whenUserRevoked = (): DocumentTemplatesStore => initialState;

export default registerStore<'documentTemplatesStore', Actions>(
	'documentTemplatesStore',
	createSliceReducer({
		DOCUMENT_TEMPLATES_FETCHED: whenDocumentTemplatesFetched,
		DOCUMENT_TEMPLATE_VERSIONS_FETCHED: whenDocumentTemplateVersionsFetched,
		DOCUMENT_TEMPLATE_VERSION_FETCHED: whenDocumentTemplateVersionFetched,
		DOCUMENT_TEMPLATE_CREATED: whenDocumentTemplateCreated,
		DOCUMENT_TEMPLATE_VERSION_UPLOADED: whenDocumentTemplateVersionUploaded,
		DOCUMENT_TEMPLATE_VERSION_SECTIONS_UPDATED: whenDocumentTemplateVersionSectionsUpdated,
		DOCUMENT_TEMPLATE_UPDATED: whenDocumentTemplateUpdated,
		DOCUMENT_TEMPLATE_ARCHIVED: whenDocumentTemplateArchived,
		TEMPLATE_ASSIGNMENTS_FETCHED: whenTemplateAssignmentsFetched,
		TEMPLATE_ASSIGNMENT_SAVED: whenTemplateAssignmentInvalidated,
		TEMPLATE_ASSIGNMENT_CLEARED: whenTemplateAssignmentInvalidated,
		FACILITY_TEMPLATE_OVERRIDES_FETCHED: whenFacilityTemplateOverridesFetched,
		FACILITY_TEMPLATE_OVERRIDE_REMOVED: whenFacilityTemplateOverrideRemoved,
		DOCUMENT_TYPE_CREATED: whenAssignedDocumentTypeCatalogueChanged,
		DOCUMENT_TYPE_ARCHIVED: whenAssignedDocumentTypeCatalogueChanged,
		DOCUMENT_TYPE_RESTORED: whenAssignedDocumentTypeCatalogueChanged,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
